import React, { useEffect, useMemo, useState } from 'react';
import { useNode } from '@craftjs/core';
import {
  Search,
  Filter,
  SlidersHorizontal,
  RotateCcw,
  ChevronLeft,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { VehicleFilters, PriceRange } from './VehicleFilters';
import { VehicleList } from './VehicleList';
import { ordenDelTenant, ordenarVehiculos, type OrdenVehiculos } from '@/lib/orden-vehiculos';
import { VehicleTypeFilter, mapCommonTypeToActual } from './VehicleTypeFilter';
import { Vehicle } from '@/utils/types';
import useClientStore from '@/store/useClientStore';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import { useEnsureVehicles } from '@/hooks/useEnsureVehicles';
import useActiveBuilderFilter from '@/store/useActiveBuilderFilter';
import { dealershipLabel } from '@/utils/functions';
import { textoEncontrados } from '@/lib/textos-usa';
import { latestEventDate, passesRecencyWindow } from '@/utils/webVisibility';
import {
  aplicarAlcanceDeCategorias,
  claveDeAlcance,
  nombresParaOfrecerComoPestana,
  type ModoAlcanceCategorias,
} from '@/lib/categoryScope';

interface VehicleStatus {
  id: number;
  name: string;
  show_in_web?: boolean;
  web_visibility_days?: number | null;
}

// Extended Vehicle interface to include the nested properties
export interface ExtendedVehicle extends Vehicle {
  vehicles_sales?:
    | Array<{ created_at: string; [key: string]: any }>
    | { created_at: string; [key: string]: any }
    | null;
  vehicles_reservations?:
    | Array<{ created_at: string; [key: string]: any }>
    | { created_at: string; [key: string]: any }
    | null;
  event_date?: string;
}

interface VehicleGridProps {
  title?: string;
  subtitle?: string;
  bgColor?: string;
  textColor?: string;
  columns?: 2 | 3 | 4;
  showStatuses?: ('Publicado' | 'Vendido' | 'Reservado')[];
  /**
   * Alcance de categorías de ESTE catálogo (ticket #86): `only` lo deja con
   * las categorías elegidas, `except` las saca. Sin configurar —lo que traen
   * todos los nodos ya publicados— muestra el inventario completo.
   */
  categoryScopeMode?: ModoAlcanceCategorias;
  categoryScopeIds?: (number | string)[];
  /**
   * Nombres de las categorías elegidas, escritos por el panel junto a los ids
   * (ticket #97). Sólo deciden qué pestañas se dibujan; el filtrado sigue
   * siendo por id y un vehículo no publicado nunca entra al listado.
   */
  categoryScopeNames?: string[];
  children?: React.ReactNode;
  showFilters?: boolean;
  cardSettings?: {
    cardBgColor: string;
    cardBorderColor: string;
    cardTextColor: string;
    cardPriceColor: string;
    cardButtonColor: string;
    cardButtonTextColor: string;
    detailsButtonText: string;
    bannerPosition: 'left' | 'right';
    pricePosition: 'overlay' | 'below-title';
    featuresConfig?: {
      feature1: 'category' | 'year' | 'fuel' | 'mileage' | 'transmission';
      feature2: 'category' | 'year' | 'fuel' | 'mileage' | 'transmission';
      feature3: 'category' | 'year' | 'fuel' | 'mileage' | 'transmission';
      feature4: 'category' | 'year' | 'fuel' | 'mileage' | 'transmission';
    };
  }[];
  newBadgeText?: string;
  filterButtonColors?: {
    buttonBgColor: string;
    buttonTextColor: string;
    buttonBorderColor: string;
    activeButtonBgColor: string;
    activeButtonTextColor: string;
    activeButtonBorderColor: string;
    containerBgColor: string;
    containerBorderColor: string;
  }[];
}

export const VehicleGrid = ({
  title = 'Nuestros vehículos',
  subtitle = 'Explora nuestro inventario de vehículos disponibles',
  bgColor = '#ffffff',
  textColor = '#333333',
  columns = 3,
  showStatuses = ['Publicado', 'Reservado', 'Vendido'],
  categoryScopeMode = 'all',
  categoryScopeIds,
  categoryScopeNames,
  showFilters = true,
  cardSettings = [
    {
      cardBgColor: '#ffffff',
      cardBorderColor: '#e5e7eb',
      cardTextColor: '#1f2937',
      cardPriceColor: '#ffffff',
      cardButtonColor: '#3b82f6', // Color del texto y borde del botón
      cardButtonTextColor: '#ffffff', // Color del fondo del botón
      detailsButtonText: 'Ver detalles',
      bannerPosition: 'right',
      pricePosition: 'overlay',
      featuresConfig: {
        feature1: 'category',
        feature2: 'year',
        feature3: 'fuel',
        feature4: 'mileage',
      },
    },
  ],
  newBadgeText = 'Nuevo',
  filterButtonColors = [
    {
      buttonBgColor: '#ffffff',
      buttonTextColor: '#3b82f6',
      buttonBorderColor: '#3b82f6',
      activeButtonBgColor: '#3b82f6',
      activeButtonTextColor: '#ffffff',
      activeButtonBorderColor: '#3b82f6',
      containerBgColor: '#ffffff',
      containerBorderColor: '#e5e7eb',
    },
  ],
  children,
}: VehicleGridProps) => {
  const {
    connectors: { connect },
    selected,
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const { client } = useClientStore();
  // Ticket #164: orden inicial que configuró la automotora.
  const ordenTenant = ordenDelTenant((client as Record<string, unknown> | null)?.web_vehicle_order);

  const { t } = useTranslation();
  // El array de ids llega con referencia nueva en cada render del editor; la
  // clave es lo que entra a las dependencias del fetch.
  const claveAlcance = claveDeAlcance(categoryScopeMode, categoryScopeIds);
  const [vehicles, setVehicles] = useState<ExtendedVehicle[]>([]);
  const [filteredVehicles, setFilteredVehicles] = useState<ExtendedVehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeVehicleType, setActiveVehicleType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  // Filtros del store
  const {
    selectedBrands,
    setSelectedBrands,
    selectedTypes,
    setSelectedTypes,
    selectedFuels,
    setSelectedFuels,
    selectedConditions,
    setSelectedConditions,
    selectedYears,
    setSelectedYears,
    selectedColors,
    setSelectedColors,
    selectedDealerships,
    setSelectedDealerships,
    priceRange,
    setPriceRange, // <- del store: (range) => void
    minMaxPrice,
    setMinMaxPrice,
    availableBrands,
    setAvailableBrands,
    availableTypes,
    setAvailableTypes,
    availableFuels,
    setAvailableFuels,
    availableConditions,
    setAvailableConditions,
    availableColors,
    setAvailableColors,
    availableDealerships,
    setAvailableDealerships,
    availableYears,
    setAvailableYears,
    clearFilters,
    sortOrder, // del store: 'price_asc' | 'price_desc' | 'date_asc' | 'date_desc'
    setSortOrder, // <- del store: (order) => void
  } = useActiveBuilderFilter();

  // === Adaptador para setPriceRange: cumple con Dispatch<SetStateAction<PriceRange>> ===
  const setPriceRangeDispatch: React.Dispatch<React.SetStateAction<PriceRange>> = (next) => {
    if (typeof next === 'function') {
      // Ejecutamos la función con el estado actual local (priceRange) y enviamos el resultado como objeto
      const computed = (next as (prev: PriceRange) => PriceRange)(priceRange);
      setPriceRange({ min: computed.min, max: computed.max });
    } else {
      setPriceRange({ min: next.min, max: next.max });
    }
  };

  // === Adaptador para setSortOrder: acepta union amplia y mapea a las soportadas por el store ===
  // Ticket #164: el vocabulario sale de `lib/orden-vehiculos` (incluye
  // 'default'). El mapeo de year_*/mileage_asc se conserva tal cual estaba —
  // es comportamiento viejo de esta grilla, no de este ticket.
  type FullSort = OrdenVehiculos;

  const setSortOrderDispatch: React.Dispatch<React.SetStateAction<FullSort>> = (value) => {
    const incoming = typeof value === 'function' ? (value as (prev: FullSort) => FullSort)(sortOrder as FullSort) : value;

    // Si tu store NO soporta year_* ni mileage_asc, mapeamos a lo más parecido o lo ignoramos.
    // Aquí elegimos mapear:
    // - year_desc/year_asc -> date_desc (más reciente primero) / date_asc (más antiguo primero)
    // - mileage_asc -> price_asc (por tener “ascendente” como criterio similar)
    let mapped: OrdenVehiculos = 'date_desc';
    if (
      incoming === 'default' ||
      incoming === 'price_asc' ||
      incoming === 'price_desc' ||
      incoming === 'date_asc' ||
      incoming === 'date_desc'
    ) {
      mapped = incoming;
    } else if (incoming === 'year_desc') {
      mapped = 'date_desc';
    } else if (incoming === 'year_asc') {
      mapped = 'date_asc';
    } else if (incoming === 'mileage_asc') {
      mapped = 'price_asc';
    }
    setSortOrder(mapped);
  };

  // Check if any filters are active
  const hasActiveFilters =
    selectedBrands.length > 0 ||
    selectedTypes.length > 0 ||
    selectedFuels.length > 0 ||
    selectedConditions.length > 0 ||
    selectedYears.length > 0 ||
    selectedColors.length > 0 ||
    selectedDealerships.length > 0 ||
    priceRange.min !== minMaxPrice.min ||
    priceRange.max !== minMaxPrice.max ||
    searchQuery.trim() !== '' ||
    activeVehicleType !== 'all';

  // Inventario del tenant. Antes cada grid hacía su propia consulta a
  // `vehicles` (la misma que ya hace `useVehiclesStore` para todo el sitio):
  // ahora lee el inventario crudo del store —todos los estados, ordenado por
  // created_at desc, filtrado a este cliente— y aplica sobre él exactamente
  // las mismas reglas de siempre. Una sola descarga por visita.
  const {
    rawVehicles: inventario,
    isLoading: inventarioCargando,
    loadedForClientId,
  } = useEnsureVehicles();
  const inventarioListo = !inventarioCargando && String(loadedForClientId) === String(client?.id);

  useEffect(() => {
    const aplicarInventario = () => {
      if (!client?.id) return;
      try {
        {
          const data = inventario.filter((v) => String(v.client_id) === String(client.id));
          // First convert to unknown to bypass type checking
          const typedData = data as unknown as Array<{
            id: any;
            brand_id: any;
            model_id: any;
            year: any;
            price: any;
            mileage: any;
            main_image: any;
            status_id: any;
            discount_percentage: any;
            created_at: any;
            state_updated_at?: string | null;
            status: VehicleStatus;
            brand: { id: any; name: any };
            model: { id: any; name: any };
            category: { id: any; name: any };
            fuel_type: { id: any; name: any };
            condition: { id: any; name: any };
            color: { id: any; name: any };
            dealership?: { id: any; name: any; address: any } | null;
            vehicles_sales?:
              | Array<{ created_at: string; [key: string]: any }>
              | { created_at: string; [key: string]: any }
              | null;
            vehicles_reservations?:
              | Array<{ created_at: string; [key: string]: any }>
              | { created_at: string; [key: string]: any }
              | null;
            event_date?: string;
          }>;

          // Fecha del evento (venta/reserva con fallback a state_updated_at /
          // updated_at) para Vendido/Reservado
          const processedData = typedData.map((vehicle) => ({
            ...vehicle,
            event_date: latestEventDate(vehicle),
          }));

          // Filter vehicles that should be visible on web
          // Use show_in_web field if defined, fallback to name-based logic for backward compatibility
          const filteredByStatus = processedData.filter((vehicle) => {
            if (!vehicle.status) return false;
            // If show_in_web is explicitly set, use it
            if (typeof vehicle.status.show_in_web === 'boolean') {
              return vehicle.status.show_in_web;
            }
            // Fallback: use name-based filtering for legacy states without show_in_web
            return showStatuses.includes(vehicle.status.name as any);
          });

          // Sort vehicles to show "Publicado" status first, then by newest
          const sortedVehicles = [...filteredByStatus].sort((a, b) => {
            if (a.status.name === 'Publicado' && b.status.name !== 'Publicado') {
              return -1;
            }
            if (a.status.name !== 'Publicado' && b.status.name === 'Publicado') {
              return 1;
            }
            // If statuses are the same, sort by created_at (newest first)
            if (a.created_at && b.created_at) {
              const dateA = new Date(a.created_at).getTime();
              const dateB = new Date(b.created_at).getTime();
              if (dateA > dateB) return -1;
              if (dateA < dateB) return 1;
            }
            return 0;
          });

          // Convert to ExtendedVehicle type
          // El alcance se aplica ACÁ, antes de derivar nada: así el contador, las
          // pestañas de tipo, el panel de filtros y el precio máximo hablan todos
          // del mismo conjunto acotado, y no de un inventario que no se ve.
          const vehiclesData = aplicarAlcanceDeCategorias(
            sortedVehicles as unknown as ExtendedVehicle[],
            categoryScopeMode,
            categoryScopeIds
          );
          setVehicles(vehiclesData);
          setFilteredVehicles(vehiclesData);

          // Extract available filter options
          const brands = [...new Set(vehiclesData.map((v) => v.brand?.name).filter(Boolean))];
          const years = [...new Set(vehiclesData.map((v) => v.year).filter(Boolean))].map(String).sort((a, b) => b.localeCompare(a));
          const types = [...new Set(vehiclesData.map((v) => v.category?.name).filter(Boolean))];
          const fuels = [...new Set(vehiclesData.map((v) => v.fuel_type?.name).filter(Boolean))];
          const conditions = [...new Set(vehiclesData.map((v) => v.condition?.name).filter(Boolean))];
          const colors = [...new Set(vehiclesData.map((v) => v.color?.name).filter(Boolean))];
          const dealershipNames = [...new Set(vehiclesData.map((v) => dealershipLabel(v.dealership)).filter(Boolean))]
            .sort((a, b) => a.localeCompare(b, 'es'));

          // Calcular el precio máximo real de los vehículos disponibles
          const availableVehicles = vehiclesData.filter((v) => v.status?.name !== 'Vendido' && v.status?.name !== 'Reservado');
          const minPrice = 0;
          const maxPrice = availableVehicles.length > 0 ? Math.max(...availableVehicles.map((v) => v.price || 0)) : 1000000000;

          setAvailableBrands(brands as string[]);
          setAvailableYears(years as string[]);
          setAvailableTypes(types as string[]);
          setAvailableFuels(fuels as string[]);
          setAvailableConditions(conditions as string[]);
          setAvailableColors(colors as string[]);
          setAvailableDealerships(dealershipNames);
          setMinMaxPrice({ min: minPrice, max: maxPrice });
        }
      } catch (err) {
        console.error('Failed to fetch vehicles:', err);
      } finally {
        setLoading(false);
      }
    };

    if (!client?.id) return;
    if (!inventarioListo) {
      setLoading(true);
      return;
    }
    aplicarInventario();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showStatuses, client?.id, claveAlcance, inventario, inventarioListo]);

  // Inicializa el rango de precio SOLO si está en valores por defecto y hay un valor real nuevo
  useEffect(() => {
    if (priceRange.min === 0 && priceRange.max === 1000000000 && minMaxPrice.max !== 1000000000) {
      setPriceRange({ min: minMaxPrice.min, max: minMaxPrice.max });
    }
  }, [minMaxPrice]);

  // Lo que alimenta la BARRA DE PESTAÑAS: el inventario que trae el catálogo más
  // las categorías elegidas a mano en modo `only` (ticket #97), para que el sitio
  // publicado dibuje las mismas pestañas que muestra el Builder.
  //
  // Deliberadamente NO alimenta el panel de filtros ni el precio máximo: ahí una
  // opción que no puede dar resultados es ruido. Y no toca el listado, que se
  // sigue armando con vehículos ya filtrados por visibilidad.
  const tiposParaPestanas = useMemo(() => {
    const elegidos = nombresParaOfrecerComoPestana(
      categoryScopeMode,
      categoryScopeIds,
      categoryScopeNames
    );
    if (elegidos.length === 0) return availableTypes;

    const yaEstan = new Set(availableTypes.map((t: string) => t.toLowerCase()));
    const faltantes = elegidos.filter((n) => !yaEstan.has(n.toLowerCase()));
    return faltantes.length === 0 ? availableTypes : [...availableTypes, ...faltantes];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableTypes, categoryScopeMode, claveAlcance, (categoryScopeNames || []).join('|')]);

  // ── La pestaña elegida tiene que seguir teniendo sentido ──
  //
  // Las pestañas son estado del componente y el componente NO se desmonta
  // cuando cambia la configuración del catálogo: sin esto, acotar el catálogo
  // a otra categoría dejaba activa una pestaña que ya no puede mostrar nada, y
  // la grilla quedaba vacía sin que se entendiera por qué.
  //
  // Si la pestaña sobrevive, se recalculan los nombres de categoría que le
  // corresponden AHORA: los de antes podían nombrar categorías que este
  // catálogo ya no incluye.
  useEffect(() => {
    if (activeVehicleType === 'all') return;
    const tiposAhora = mapCommonTypeToActual(activeVehicleType, tiposParaPestanas);
    if (tiposAhora.length === 0) {
      setActiveVehicleType('all');
      setSelectedTypes([]);
      return;
    }
    setSelectedTypes((previos) => {
      const igual =
        previos.length === tiposAhora.length &&
        tiposAhora.every((t) => previos.includes(t));
      return igual ? previos : tiposAhora;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiposParaPestanas, activeVehicleType]);

  // Reset all filters
  const resetAllFilters = () => {
    setPriceRange({ min: minMaxPrice.min, max: minMaxPrice.max });
    setSelectedBrands([]);
    setSelectedYears([]);
    setSelectedTypes([]);
    setSelectedFuels([]);
    setSelectedConditions([]);
    setSelectedColors([]);
    setSelectedDealerships([]);
    activeVehicleType !== 'all' && setActiveVehicleType('all');
    setSearchQuery('');
  };

  // Apply filters when any filter value changes
  useEffect(() => {
    if (vehicles.length === 0) return;

    let filtered = [...vehicles];

    // Search filter - check brand, model, category
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase().trim();
      const searchTerms = query.split(' ').filter((term) => term.length > 0);

      filtered = filtered.filter((v) => {
        return searchTerms.every((term) => {
          const brandMatch = v.brand?.name?.toLowerCase().includes(term);
          const modelMatch = v.model?.name?.toLowerCase().includes(term);
          const categoryMatch = v.category?.name?.toLowerCase().includes(term);
          const yearMatch = v.year?.toString().includes(term);

          return brandMatch || modelMatch || categoryMatch || yearMatch;
        });
      });
    }

    // Filter by price range
    filtered = filtered.filter((v) => v.price !== undefined && v.price >= priceRange.min && v.price <= priceRange.max);

    // Filter by brand
    if (selectedBrands.length > 0) {
      filtered = filtered.filter((v) => v.brand?.name && selectedBrands.includes(v.brand.name as string));
    }

    // Filter by type (category)
    if (selectedTypes.length > 0) {
      filtered = filtered.filter((v) => v.category?.name && selectedTypes.includes(v.category.name as string));
    }

    // Filter by fuel type
    if (selectedFuels.length > 0) {
      filtered = filtered.filter((v) => v.fuel_type?.name && selectedFuels.includes(v.fuel_type.name as string));
    }

    // Filter by condition
    if (selectedConditions.length > 0) {
      filtered = filtered.filter((v) => v.condition?.name && selectedConditions.includes(v.condition.name as string));
    }

    // Filter by color
    if (selectedColors.length > 0) {
      filtered = filtered.filter((v) => v.color?.name && selectedColors.includes(v.color.name as string));
    }

    // Filter by dealership (sucursal)
    if (selectedDealerships.length > 0) {
      filtered = filtered.filter((v) => selectedDealerships.includes(dealershipLabel(v.dealership)));
    }

    // Ventana de recencia para "Vendido"/"Reservado": web_visibility_days del
    // estado, o 3 días si el tenant no la configuró.
    filtered = filtered.filter((vehicle) => passesRecencyWindow(vehicle));

    // Actualizar los años disponibles basados en los vehículos filtrados
    const availableYearsFromFiltered = [...new Set(filtered.map((v) => v.year).filter(Boolean))]
      .map(String)
      .sort((a, b) => b.localeCompare(a));
    setAvailableYears(availableYearsFromFiltered);

    // Volver a filtrar por año si hay años seleccionados
    if (selectedYears.length > 0) {
      filtered = filtered.filter((v) => v.year && selectedYears.includes(String(v.year)));
    }

    // Ordenamiento: una sola implementación para todo el sitio
    // (`lib/orden-vehiculos`). En 'default' manda el orden que configuró la
    // automotora (ticket #164); si el visitante eligió precio o fecha, gana lo
    // suyo.
    filtered = ordenarVehiculos(filtered, sortOrder, ordenTenant);

    setFilteredVehicles(filtered);
  }, [
    vehicles,
    searchQuery,
    priceRange,
    selectedBrands,
    selectedTypes,
    selectedFuels,
    selectedConditions,
    selectedColors,
    selectedDealerships,
    selectedYears,
    sortOrder,
    // Hidrata después del primer render: sin esto la grilla se queda con el
    // default "newest" aunque la automotora haya configurado el otro.
    ordenTenant,
  ]);

  // Generate status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Publicado':
        return 'bg-green-100 text-green-800';
      case 'Vendido':
        return 'bg-red-100 text-red-800';
      case 'Reservado':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Toggle filter selection
  const toggleFilter = (
    filter: string,
    type: 'brand' | 'year' | 'type' | 'fuel' | 'condition' | 'color' | 'dealership'
  ) => {
    switch (type) {
      case 'brand':
        setSelectedBrands((prev) => (prev.includes(filter) ? prev.filter((b) => b !== filter) : [...prev, filter]));
        break;
      case 'year':
        setSelectedYears((prev) => (prev.includes(filter) ? prev.filter((y) => y !== filter) : [...prev, filter]));
        break;
      case 'type':
        setSelectedTypes((prev) => (prev.includes(filter) ? prev.filter((t) => t !== filter) : [...prev, filter]));
        break;
      case 'fuel':
        setSelectedFuels((prev) => (prev.includes(filter) ? prev.filter((f) => f !== filter) : [...prev, filter]));
        break;
      case 'condition':
        setSelectedConditions((prev) => (prev.includes(filter) ? prev.filter((c) => c !== filter) : [...prev, filter]));
        break;
      case 'color':
        setSelectedColors((prev) => (prev.includes(filter) ? prev.filter((c) => c !== filter) : [...prev, filter]));
        break;
      case 'dealership':
        setSelectedDealerships((prev) => (prev.includes(filter) ? prev.filter((d) => d !== filter) : [...prev, filter]));
        break;
    }
  };

  // Create a ref handler that returns void to match expected type
  const refHandler = (element: HTMLDivElement | null) => {
    if (element) {
      connect(element);
    }
  };

  return (
    <div
      ref={refHandler}
      style={{
        background: bgColor,
        color: textColor,
        padding: '40px 0',
        position: 'relative',
        border: selected ? '1px dashed #1e88e5' : '1px solid transparent',
      }}
      className='w-full VehicleGrid vehicles-section'
      data-section='vehicles'
      id='vehicles-section'
    >
      <div className='max-w-7xl mx-auto px-4 sm:px-6'>
        {/* Header Section */}
        <div className='text-center mb-8'>
          <h2 style={{ color: textColor }} className='text-3xl font-bold mb-3 text-center'>
            {title}
          </h2>
          <p style={{ color: textColor }} className='text-base text-gray-600 mb-6 text-center max-w-2xl mx-auto'>
            {subtitle}
          </p>
        </div>

        {loading ? (
          <div className='flex justify-center items-center h-60'>
            <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500'></div>
          </div>
        ) : (
          <div>
            {/* Search and Filter Bar */}
            <div className='mb-6 flex flex-col sm:flex-row gap-4'>
              <div className='relative flex-grow'>
                <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400' size={18} />
                <Input
                  type='text'
                  placeholder={t('site.vehicles.searchPlaceholder')}
                  className='pl-10 pr-4 py-2 w-full rounded-lg border border-gray-200'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className='flex gap-3'>
                {/* Filter button for mobile */}
                <Sheet open={isMobileFilterOpen} onOpenChange={setIsMobileFilterOpen}>
                  <SheetTrigger asChild>
                    <Button
                      variant='outline'
                      className='flex items-center gap-2 md:hidden border border-black dark:border-white bg-white dark:bg-black text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-900 transition-all duration-200 ease-in-out transform hover:scale-105'
                    >
                      <Filter size={16} />
                      <span>{t('site.vehicles.filters')}</span>
                      {hasActiveFilters && (
                        <span className='bg-black text-white dark:bg-white dark:text-black text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center'>
                          !
                        </span>
                      )}
                    </Button>
                  </SheetTrigger>
                  <SheetContent side='right' className='w-[85vw] sm:max-w-md p-0 overflow-auto'>
                    <SheetHeader className='px-4 py-3 border-b border-gray-100 sticky top-0 bg-white z-10'>
                      <div className='flex justify-between items-center'>
                        <SheetTitle className='text-left'>{t('site.vehicles.filters')}</SheetTitle>
                        <div className='flex items-center gap-2'>
                          {hasActiveFilters && (
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={() => {
                                resetAllFilters();
                                setIsMobileFilterOpen(false);
                              }}
                              className='text-blue-600 hover:text-blue-800 text-xs'
                            >
                              <RotateCcw size={14} className='mr-1' />
                              {t('site.vehicles.reset')}
                            </Button>
                          )}
                          <SheetClose asChild>
                            <Button variant='outline' size='sm'>
                              <ChevronLeft size={16} className='mr-1' />
                              {t('site.vehicles.back')}
                            </Button>
                          </SheetClose>
                        </div>
                      </div>
                    </SheetHeader>

                    <div className='p-4'>
                      <VehicleFilters
                        priceRange={priceRange}
                        setPriceRange={setPriceRangeDispatch}
                        minMaxPrice={minMaxPrice}
                        selectedBrands={selectedBrands}
                        setSelectedBrands={setSelectedBrands}
                        availableBrands={availableBrands}
                        selectedYears={selectedYears}
                        setSelectedYears={setSelectedYears}
                        availableYears={availableYears}
                        selectedTypes={selectedTypes}
                        setSelectedTypes={setSelectedTypes}
                        availableTypes={availableTypes}
                        selectedFuels={selectedFuels}
                        setSelectedFuels={setSelectedFuels}
                        availableFuels={availableFuels}
                        selectedConditions={selectedConditions}
                        setSelectedConditions={setSelectedConditions}
                        availableConditions={availableConditions}
                        selectedColors={selectedColors}
                        setSelectedColors={setSelectedColors}
                        availableColors={availableColors}
                        selectedDealerships={selectedDealerships}
                        availableDealerships={availableDealerships}
                        toggleFilter={toggleFilter}
                        activeVehicleType={activeVehicleType}
                        resetAllFilters={resetAllFilters}
                      />
                    </div>
                  </SheetContent>
                </Sheet>

                {/* Filter toggle for desktop */}
                {showFilters && (
                  <Button
                    variant='outline'
                    className='hidden md:flex items-center gap-2 border border-black dark:border-white bg-white dark:bg-black text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-900 transition-all duration-200 ease-in-out transform hover:scale-105'
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                  >
                    {isFilterOpen ? <SlidersHorizontal size={16} /> : <Filter size={16} />}
                    <span>{isFilterOpen ? t('site.vehicles.hideFilters') : t('site.vehicles.showFilters')}</span>
                    {hasActiveFilters && !isFilterOpen && (
                      <span className='bg-black text-white dark:bg-white dark:text-black text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center'>
                        !
                      </span>
                    )}
                  </Button>
                )}

                {hasActiveFilters && (
                  <Button
                    variant='outline'
                    className='text-sm text-black border border-black bg-white hover:bg-black hover:text-white transition-all duration-200 ease-in-out transform hover:scale-105'
                    onClick={resetAllFilters}
                  >
                    <RotateCcw size={14} className='mr-1' />
                    {t('site.vehicles.clear')}
                  </Button>
                )}
              </div>
            </div>

            {/* Vehicle Type Filter - Horizontal above grid */}
            <VehicleTypeFilter
              activeVehicleType={activeVehicleType}
              setActiveVehicleType={setActiveVehicleType}
              availableTypes={tiposParaPestanas}
              setSelectedTypes={setSelectedTypes}
              filterButtonColors={filterButtonColors}
            />

            <div className='flex flex-col md:flex-row gap-6'>
              {/* Filters Section - Desktop */}
              {showFilters && isFilterOpen && (
                <div className='hidden md:block'>
                  <VehicleFilters
                    priceRange={priceRange}
                    setPriceRange={setPriceRangeDispatch}
                    minMaxPrice={minMaxPrice}
                    selectedBrands={selectedBrands}
                    setSelectedBrands={setSelectedBrands}
                    availableBrands={availableBrands}
                    selectedYears={selectedYears}
                    setSelectedYears={setSelectedYears}
                    availableYears={availableYears}
                    selectedTypes={selectedTypes}
                    setSelectedTypes={setSelectedTypes}
                    availableTypes={availableTypes}
                    selectedFuels={selectedFuels}
                    setSelectedFuels={setSelectedFuels}
                    availableFuels={availableFuels}
                    selectedConditions={selectedConditions}
                    setSelectedConditions={setSelectedConditions}
                    availableConditions={availableConditions}
                    selectedColors={selectedColors}
                    setSelectedColors={setSelectedColors}
                    availableColors={availableColors}
                    selectedDealerships={selectedDealerships}
                    availableDealerships={availableDealerships}
                    toggleFilter={toggleFilter}
                    activeVehicleType={activeVehicleType}
                    resetAllFilters={resetAllFilters}
                  />
                </div>
              )}

              {/* Results Section */}
              <div
                className={`flex-1 ${filteredVehicles.length === 0 ? 'flex justify-center items-center min-h-[300px]' : ''}`}
              >
                {filteredVehicles.length === 0 ? (
                  <div className='text-center p-8 bg-gray-50 rounded-xl'>
                    <div className='text-gray-400 mb-3'>
                      <Search size={48} className='mx-auto opacity-50' />
                    </div>
                    <h3 className='text-lg font-medium text-gray-700 mb-2'>{t('site.vehicles.noResultsTitle')}</h3>
                    <p className='text-gray-500 mb-4'>{t('site.vehicles.noResultsTextSearch')}</p>
                    <Button variant='outline' onClick={resetAllFilters}>
                      {t('site.vehicles.removeAllFilters')}
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className='mb-4 flex justify-between items-center'>
                      <p className='text-sm text-gray-500'>
                        <span className='font-medium text-gray-700'>{filteredVehicles.length}</span>{' '}
                        {textoEncontrados(filteredVehicles.length, t('site.vehicles.found'), client?.country)}
                      </p>
                    </div>

                    <VehicleList
                      vehicles={filteredVehicles}
                      columns={columns}
                      getStatusColor={getStatusColor}
                      sortOrder={sortOrder as any}
                      setSortOrder={setSortOrderDispatch}
                      cardSettings={cardSettings}
                      newBadgeText={newBadgeText}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {children}
      </div>
    </div>
  );
};

VehicleGrid.craft = {
  displayName: 'VehicleGrid',
  props: {
    title: 'Nuestros vehículos',
    subtitle: 'Explora nuestro inventario de vehículos disponibles',
    bgColor: '#ffffff',
    textColor: '#333333',
    columns: 3,
    showStatuses: ['Publicado', 'Reservado', 'Vendido'],
    showFilters: true,
    cardSettings: [
      {
        cardBgColor: '#ffffff',
        cardBorderColor: '#e5e7eb',
        cardTextColor: '#1f2937',
        cardPriceColor: '#ffffff',
        cardButtonColor: '#3b82f6', // Color del texto y borde del botón
        cardButtonTextColor: '#ffffff', // Color del fondo del botón
        detailsButtonText: 'Ver detalles',
        bannerPosition: 'right',
        pricePosition: 'overlay',
        featuresConfig: {
          feature1: 'category',
          feature2: 'year',
          feature3: 'fuel',
          feature4: 'mileage',
        },
      },
    ],
    newBadgeText: 'Nuevo',
    filterButtonColors: [
      {
        buttonBgColor: '#ffffff',
        buttonTextColor: '#3b82f6',
        buttonBorderColor: '#3b82f6',
        activeButtonBgColor: '#3b82f6',
        activeButtonTextColor: '#ffffff',
        activeButtonBorderColor: '#3b82f6',
        containerBgColor: '#ffffff',
        containerBorderColor: '#e5e7eb',
      },
    ],
  },
  related: {
    // Settings component will be external
  },
  rules: {
    canDrag: () => true,
    canDrop: () => true,
    canMoveIn: () => true,
  },
  isCanvas: true,
};
